import React, { useMemo } from 'react'
import ReactEChartsCore from '../../Component/Charts/EChartsCore.js'
import * as echarts from 'echarts/core'
import type { ComposeOption } from 'echarts/core'
import { BarChart as EBarChart, type BarSeriesOption } from 'echarts/charts'
import {
  GridComponent,
  TooltipComponent,
  type GridComponentOption,
  type TooltipComponentOption
} from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'
import type { Datum } from './statsAggregations.js'

echarts.use([EBarChart, GridComponent, TooltipComponent, CanvasRenderer])

type EChartsOption = ComposeOption<BarSeriesOption | GridComponentOption | TooltipComponentOption>

interface Props {
  data: Datum[]
  color?: string
  height?: number
  /** Rotate x labels (for long categorical names). */
  rotateLabels?: boolean
  /** Fix the y-axis max (so side-by-side period versions are visually comparable). */
  yMax?: number
}

/** A simple categorical/histogram bar chart used across the admin stats panel. */
export const StatsBarChart: React.FC<Props> = ({ data, color, height, rotateLabels, yMax }) => {
  const option: EChartsOption = useMemo(
    () => ({
      backgroundColor: 'transparent',
      animation: false,
      tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' }, confine: true },
      grid: { top: 16, bottom: rotateLabels ? 96 : 32, left: 48, right: 16, containLabel: true },
      xAxis: {
        type: 'category',
        data: data.map((d) => d.name),
        axisLabel: { interval: 0, rotate: rotateLabels ? 40 : 0, fontSize: 10 }
      },
      yAxis: { type: 'value', minInterval: 1, max: yMax },
      series: [{ type: 'bar', data: data.map((d) => d.value), itemStyle: { color: color ?? '#3b82f6' } }]
    }),
    [data, color, rotateLabels, yMax]
  )

  return (
    <ReactEChartsCore
      echarts={echarts}
      option={option}
      notMerge
      lazyUpdate
      style={{ width: '100%', height: height ?? 320 }}
    />
  )
}
